import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Box, Typography, TextField, InputAdornment, CircularProgress,
    Pagination, ToggleButtonGroup, ToggleButton, Chip, Divider, Paper, Button,
    useTheme, useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import AllInboxOutlinedIcon from "@mui/icons-material/AllInboxOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import ConversationListItem from "./ConversationListItem";
import ConversationDrawer from "./ConversationDrawer";
import TemplateMessageDialog from "./TemplateMessageDialog";
import WhatsappUsageReport from "./WhatsappUsageReport";
import PushNotificationsControl from "./PushNotificationsControl";
import whatsappServices from "./whatsappServices";
import { getConsumer } from "../../services/cable";
import { SIN_ZOOM_IPHONE } from "./sinZoomIphone";

const DRAWER_WIDTH = 480;

// Filtros de la barra superior
const FILTERS = [
    { value: "all",    label: "Todos",   icon: <AllInboxOutlinedIcon sx={{ fontSize: 14 }} /> },
    { value: "bot",    label: "Bot",     icon: <SmartToyOutlinedIcon sx={{ fontSize: 14 }} />, mode: "bot" },
    { value: "human",  label: "Humano",  icon: <PersonOutlineOutlinedIcon sx={{ fontSize: 14 }} />, mode: "human" },
    { value: "ai",     label: "Lead IA", icon: <AutoAwesomeOutlinedIcon sx={{ fontSize: 14 }} />, salesFlow: "true" },
    { value: "archived", label: "Archivados", icon: <ArchiveOutlinedIcon sx={{ fontSize: 14 }} />, archived: "true" },
    { value: "usage",  label: "Uso clientes", icon: <AssessmentOutlinedIcon sx={{ fontSize: 14 }} /> },
];

export default function WhatsappInbox() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [conversations, setConversations] = useState([]);
    const [loading, setLoading]             = useState(false);
    const [page, setPage]                   = useState(1);
    const [totalPages, setTotalPages]       = useState(1);
    const [totalCount, setTotalCount]       = useState(0);
    const [search, setSearch]               = useState("");
    const [searchInput, setSearchInput]     = useState("");
    const [filter, setFilter]               = useState("all");
    const [selected, setSelected]           = useState(null);
    const [drawerOpen, setDrawerOpen]       = useState(false);
    const [refreshToken, setRefreshToken]   = useState(0);
    const [newMessageOpen, setNewMessageOpen] = useState(false);

    // El permiso y la suscripción push viven en PushNotificationsControl
    useEffect(() => {
        // Clear badge when the inbox is open
        if (navigator.clearAppBadge) navigator.clearAppBadge().catch(() => {});
        if (navigator.serviceWorker?.controller) {
            navigator.serviceWorker.controller.postMessage({ type: "CLEAR_BADGE" });
        }
    }, []);

    // Cada carga lleva un número; solo se aplica la respuesta de la última. Sin esto, si
    // dos cargas se cruzaban (el aviso de un mensaje y la consulta periódica), la más
    // vieja podía llegar después y dejar la lista sin el mensaje nuevo.
    const requestSeq = useRef(0);

    // background: refresco por un aviso o por la consulta periódica. No vacía la lista
    // ni muestra el spinner: llegan varios avisos por mensaje (el entrante y los estados
    // enviado/entregado/leído de la respuesta) y la lista parpadeaba con cada uno.
    const loadConversations = useCallback(({ background = false } = {}) => {
        // El tab "Uso clientes" no lista conversaciones; renderiza su propia tabla.
        if (filter === "usage") {
            setConversations([]);
            setTotalPages(1);
            setTotalCount(0);
            setLoading(false);
            return;
        }
        const seq = ++requestSeq.current;
        if (!background) setLoading(true);
        const activeFilter = FILTERS.find(f => f.value === filter) || {};
        const params = {
            page,
            search: search || undefined,
            mode:       activeFilter.mode,
            sales_flow: activeFilter.salesFlow,
            archived:   activeFilter.archived,
        };
        whatsappServices.getConversations(params)
            .then(data => {
                if (seq !== requestSeq.current) return;
                setConversations(data.data || []);
                setTotalPages(data.meta?.total_pages || 1);
                setTotalCount(data.meta?.total_count || 0);
            })
            .catch(() => {})
            .finally(() => {
                if (seq === requestSeq.current) setLoading(false);
            });
    }, [page, search, filter]);

    useEffect(() => {
        loadConversations();
    }, [loadConversations]);

    // Real-time updates via WebSocket (fallback to polling)
    const loadRef = useRef(loadConversations);
    loadRef.current = loadConversations;

    useEffect(() => {
        const refresh = () => {
            loadRef.current({ background: true });
            setRefreshToken(t => t + 1);
        };

        const consumer = getConsumer();
        let subscription = null;

        if (consumer) {
            subscription = consumer.subscriptions.create("WhatsappInboxChannel", {
                received: refresh,
                // También al reconectar: los avisos que llegaron mientras la conexión
                // estaba caída (un deploy, el computador suspendido, la red) no se
                // reenvían, y la lista quedaba vieja hasta la consulta periódica.
                connected: refresh,
            });
        }

        // Al volver a la pestaña. En segundo plano el navegador frena la consulta
        // periódica (Chrome la deja en una por minuto), así que al mirar la lista
        // puede estar atrasada.
        const onVisible = () => {
            if (document.visibilityState === "visible") refresh();
        };
        document.addEventListener("visibilitychange", onVisible);

        // Fallback polling in case WebSocket is unavailable
        const interval = setInterval(refresh, 30000);

        return () => {
            if (subscription) subscription.unsubscribe();
            document.removeEventListener("visibilitychange", onVisible);
            clearInterval(interval);
        };
    }, []);

    const handleSearchKeyDown = (e) => {
        if (e.key === "Enter") {
            setSearch(searchInput);
            setPage(1);
        }
    };

    const handleFilterChange = (_, newFilter) => {
        if (!newFilter) return;
        setFilter(newFilter);
        setPage(1);
    };

    const handleSelectConversation = (conv) => {
        setSelected(conv);
        setDrawerOpen(true);
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
    };

    const handleConversationUpdated = () => {
        loadConversations();
    };

    // Archivar o desarchivar la saca de la lista que se está mirando.
    const handleArchivedChange = () => {
        setDrawerOpen(false);
        setSelected(null);
        loadConversations();
    };

    // Abrir la conversación recién iniciada. Queda en modo humano y desarchivada, así que
    // se ve en "Todos" (o en "Humano").
    const handleNewMessageSent = (conv) => {
        setNewMessageOpen(false);
        if (filter !== "all" && filter !== "human") setFilter("all");
        setPage(1);
        setSelected(conv);
        setDrawerOpen(true);
        loadConversations();
    };

    const isUsage = filter === "usage";

    return (
        <Box
            sx={{
                display: "flex",
                height: "calc(100vh - 64px)", // ajusta si tu header es más alto
                overflow: "hidden",
                bgcolor: "#f5f5f5",
                ...SIN_ZOOM_IPHONE,
            }}
        >
            {/* ── PANEL IZQUIERDO: lista ─────────────────────── */}
            <Box
                sx={{
                    width: drawerOpen && !isMobile ? `calc(100% - ${DRAWER_WIDTH}px)` : "100%",
                    transition: "width 0.2s",
                    display: "flex",
                    flexDirection: "column",
                    bgcolor: "#fff",
                    borderRight: "1px solid #e0e0e0",
                    overflow: "hidden",
                }}
            >
                {/* Header */}
                <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 16 }}>
                            WhatsApp Inbox
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {!isUsage && (
                                <Chip
                                    label={`${totalCount} conv.`}
                                    size="small"
                                    sx={{ bgcolor: "#ede7f6", color: "#7b1fa2", fontSize: 11, height: 20 }}
                                />
                            )}
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={<AddCommentOutlinedIcon sx={{ fontSize: 16 }} />}
                                onClick={() => setNewMessageOpen(true)}
                                sx={{ fontSize: 11, py: 0.3, textTransform: "none" }}
                            >
                                Nuevo mensaje
                            </Button>
                        </Box>
                    </Box>
                    <Box sx={{ mb: 1 }}>
                        <PushNotificationsControl />
                    </Box>

                    {/* Búsqueda */}
                    {!isUsage && (
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Buscar por nombre, teléfono… (Enter)"
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 16, color: "#aaa" }} />
                                    </InputAdornment>
                                ),
                                sx: { fontSize: 13, borderRadius: 2 },
                            }}
                            sx={{ mb: 1 }}
                        />
                    )}

                    {/* Filtros */}
                    <ToggleButtonGroup
                        value={filter}
                        exclusive
                        onChange={handleFilterChange}
                        size="small"
                        sx={{ flexWrap: "wrap" }}
                    >
                        {FILTERS.map(f => (
                            <ToggleButton
                                key={f.value}
                                value={f.value}
                                sx={{
                                    fontSize: 11, px: 1.2, py: 0.3,
                                    display: "flex", gap: 0.4, alignItems: "center",
                                }}
                            >
                                {f.icon}
                                {f.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>

                <Divider />

                {/* Área principal: reporte de uso o lista de conversaciones */}
                <Box sx={{ flex: 1, overflowY: "auto" }}>
                    {isUsage ? (
                        <WhatsappUsageReport />
                    ) : (
                        <>
                            {loading && (
                                <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
                                    <CircularProgress size={28} />
                                </Box>
                            )}

                            {!loading && conversations.length === 0 && (
                                <Typography variant="body2" sx={{ textAlign: "center", color: "#bbb", mt: 5 }}>
                                    No hay conversaciones
                                </Typography>
                            )}

                            {!loading && conversations.map(conv => (
                                <ConversationListItem
                                    key={conv.id}
                                    conversation={conv}
                                    selected={selected?.id === conv.id}
                                    onClick={handleSelectConversation}
                                />
                            ))}
                        </>
                    )}
                </Box>

                {/* Paginación */}
                {!isUsage && totalPages > 1 && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 1, borderTop: "1px solid #f0f0f0" }}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(_, v) => setPage(v)}
                            size="small"
                            color="primary"
                        />
                    </Box>
                )}
            </Box>

            {/* ── DRAWER DERECHO: detalle ────────────────────── */}
            <ConversationDrawer
                key={selected?.id || "none"}
                open={drawerOpen}
                conversation={selected}
                onClose={handleDrawerClose}
                onConversationUpdated={handleConversationUpdated}
                onArchivedChange={handleArchivedChange}
                refreshToken={refreshToken}
            />

            <TemplateMessageDialog
                open={newMessageOpen}
                onClose={() => setNewMessageOpen(false)}
                onSent={handleNewMessageSent}
            />
        </Box>
    );
}
